import { v4 as uuidv4 } from 'uuid';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { authConfig } from '@/services/auth/auth-config';
import { formatSum } from '@/services/currency/format-sum';
import { fromMinorUnits, toMinorUnits } from '@/services/currency/minor-units';
import {
  allocateCopyAmounts,
  CAPTURE_AFTER_PRINT,
  isPaymentAccepted,
} from '@/services/print-copy-payment';
import { clientEvents } from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { idleWatcher } from './IdleWatcher';

/**
 * @typedef {Error & { code?: string, failure?: object }} PaymentError
 * Error type that may include a code property (used by Stripe errors and custom payment errors)
 */

/**
 * @typedef {{
 *   now: () => number,
 *   setTimeout: (callback: () => void, ms: number) => unknown,
 *   clearTimeout: (handle: unknown) => void,
 * }} PaymentClock
 */

/** @type {PaymentClock} */
const SYSTEM_CLOCK = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) => clearTimeout(handle),
};

// The marker stays in localStorage, thus the next page load can reset a
// reader action that a page before the reload started.
const READER_ACTION_MARKER_KEY = 'kiosk_payment_reader_action';

// A reader reset waits this long for the answer of a send to the reader. The
// backend stops a reader call after 10 s, thus after this time the action of
// the send is on the reader or not at all.
const READER_SEND_WAIT_MS = 15_000;

/**
 * @typedef {{
 *   payment_intent_id: string | null,
 *   reader_id: string,
 *   account_id: string | null,
 * }} ReaderActionMarker
 */

/** @returns {ReaderActionMarker | null} */
function readReaderActionMarker() {
  try {
    const marker = JSON.parse(
      localStorage.getItem(READER_ACTION_MARKER_KEY) ?? 'null'
    );
    return typeof marker?.reader_id === 'string' ? marker : null;
  } catch (_e) {
    return null;
  }
}

/** @param {ReaderActionMarker} marker */
function writeReaderActionMarker(marker) {
  try {
    localStorage.setItem(READER_ACTION_MARKER_KEY, JSON.stringify(marker));
  } catch (_e) {
    // A kiosk without localStorage cannot reset the reader after a reload.
  }
}

// A reset clears only the marker that it read. A marker of a subsequent
// payment stays for the reset of that payment.
/** @param {ReaderActionMarker | null} resetMarker */
function clearReaderActionMarker(resetMarker) {
  const current = readReaderActionMarker();
  if (
    !current ||
    current.payment_intent_id !== resetMarker?.payment_intent_id
  ) {
    return;
  }
  try {
    localStorage.removeItem(READER_ACTION_MARKER_KEY);
  } catch (_e) {
    // The next reset tries again.
  }
}

function paymentError(code, message, failure = null, cause = undefined) {
  const error = /** @type {PaymentError} */ (
    new Error(message, cause === undefined ? undefined : { cause })
  );
  error.code = code;
  if (failure) {
    error.failure = failure;
  }
  return error;
}

// The backend accepts a declaration of 1 to 100 copies.
const MAX_DECLARED_COPIES = 100;

function isSameDeclaration(declared, other) {
  if (declared === null || other === null) {
    return declared === other;
  }
  return (
    declared.length === other.length &&
    declared.every((amount, index) => amount === other[index])
  );
}

// The declaration in the metadata of a payment intent, or null when the
// intent captures at the payment. The backend writes the amounts as
// "500,500".
function declarationOf(paymentIntent) {
  const metadata = paymentIntent?.metadata ?? {};
  if (metadata.capture !== CAPTURE_AFTER_PRINT) {
    return null;
  }
  return String(metadata.copy_amounts ?? '')
    .split(',')
    .filter((value) => value !== '')
    .map(Number);
}

function isSameFailure(failure, other) {
  return (
    Boolean(other) &&
    failure.category === other.category &&
    failure.code === other.code &&
    (failure.decline_code ?? null) === (other.decline_code ?? null)
  );
}

// The send-to-reader response can carry the failure of a previous tap on the
// same intent. The backend gives each tap its own attempt_id. A backend of a
// previous release does not, thus the poll then compares the failure fields,
// and a card that the poll found in processing makes the failure new.
function isNewCardFailure(failure, failureAtSend, hasPaymentStarted) {
  if (failure.attempt_id && failureAtSend?.attempt_id) {
    return failure.attempt_id !== failureAtSend.attempt_id;
  }
  return hasPaymentStarted || !isSameFailure(failure, failureAtSend);
}

// Stripe puts no action on a reader that is offline. Thus the kiosk sends no
// reset after this refusal: on an offline reader the reset also fails after
// approximately 7 s, and it can overlap the send of the retry.
function isReaderOffline(failure) {
  return failure?.code === 'terminal_reader_offline';
}

export class Payment {
  // Tracks kiosk slugs we've already warned about missing reader_id this
  // page-load. Static (class-level) on purpose: the missing reader_id is a
  // per-kiosk *config* state — not something the user can resolve
  // in-session — so re-firing the Sentry warning on every config refresh
  // is pure noise.
  static _warnedReaderlessKiosks = new Set();

  // KioskConfig changes the config only between sessions, so these reads stay
  // the same for the whole session.
  /** @returns {number} */
  get pricePerPortrait() {
    return store.kiosk?.price ?? 0;
  }

  // `||` and not `??`: a price of 0 means "not set" and takes the full price.
  /** @returns {number} */
  get pricePerGroupPortrait() {
    return store.kiosk?.price_multi || this.pricePerPortrait;
  }

  /** @returns {number} */
  get pricePerCopy() {
    return store.kiosk?.price_multi_copy || this.pricePerPortrait;
  }

  // A misconfigured row can have currency: null.
  /** @returns {string | null} */
  get currency() {
    const currency = store.kiosk?.currency;
    return currency && typeof currency === 'string' ? currency : null;
  }

  // Without a reader id there is nowhere to send the intent: the flow skips
  // the payment.
  /** @returns {boolean} */
  get isStripePayment() {
    return this._isStripePaymentFor(store.kiosk);
  }

  /** @returns {string} */
  get paymentDestination() {
    return store.kiosk?.payment_destination || 'platform';
  }

  /** @returns {string | null} */
  get stripeAccountId() {
    return store.kioskPartner?.stripe_account_id || null;
  }

  _isStripePaymentConfigured(kiosk) {
    const override = authConfig.getStripePaymentOverride();
    return Boolean(
      override !== null ? override : kiosk?.is_stripe_terminal_payment
    );
  }

  _isStripePaymentFor(kiosk) {
    return (
      this._isStripePaymentConfigured(kiosk) &&
      Boolean(authConfig.getStripeReaderId())
    );
  }

  isAborted = false;

  // Prevents double charges from rapid clicks
  @reactive
  isPaymentInProgress = false;

  // The failed payments of the visit. The card screen gives three tries for
  // each visit, not for each time that it opens. The session end clears it.
  failedPaymentCount = 0;

  _idleWatcherPause = null;
  _pollingCleanup = null; // Cleanup handle for active polling setTimeout
  _cancelInFlight = null; // Promise for in-flight cancelPaymentOnReader API call
  /** @type {{ paymentIntentId: string, tookAction: Promise<boolean> } | null} */
  _readerSend = null; // The send to the reader that has no answer; see _watchReaderSend
  /** @type {Promise<void> | null} */
  _attemptEnd = null; // Settles when the last processPayment() attempt ends; see whenIdle
  _pageLoadResetDone = false;

  // True iff this session reached the point where a payment intent was
  // dispatched to the physical Stripe terminal reader. Gates the
  // cancel-on-reader API call in resetForNewSession() so a session that
  // never started a payment (user clicks logo from home screen, never
  // takes a photo) does not send a reader reset that has no purpose.
  _paymentIntentSentToReader = false;

  // The payment intent on the reader before the card paid. When the card
  // window ends, a payment fails or the visitor cancels, the kiosk asks the
  // backend to release a held amount of this intent.
  _readerPaymentIntentId = null;

  // The held intent of a declared order after the card paid and before a
  // hold step accepted it. A cancel or the session end releases it. The reset
  // at the end of a failed payment keeps it, because a retry accepts it.
  _paidHoldIntentId = null;

  // Monotonic generation counter. Incremented on each processPayment() call.
  // Stale poll callbacks (from a previous payment attempt) compare their captured
  // generation against the current value and bail out instead of resolving/rejecting,
  // preventing cross-payment interference.
  _paymentGeneration = 0;

  // Tracks the last payment intent that reached requires_capture/succeeded state.
  // Used on retry to check if the previous capture actually succeeded server-side
  // before creating a new payment intent, preventing double charges from lost responses.
  _lastCaptureReadyIntentId = null;
  _lastCaptureReadyAccountId = null;
  // Idempotency key + params of the last createPaymentIntent attempt.
  // Survives a failed attempt so a retry with identical params can reuse
  // the key: Stripe then returns the SAME intent instead of minting a
  // second one the refund path could never see (double-charge risk).
  _lastAttemptParams = null;

  // Order identity sent at create and at capture or hold. It stays the same
  // over the retries of one order, and an accepted order clears it: the
  // backend allows a second payment on a paid session only when its key
  // differs, and the second payment is how a failed copy prints again.
  _checkoutKey = null;

  // The last differentiated failure the status poll observed (card decline,
  // reader fault, …), so a later captureException can report why the terminal
  // was failing. Reset at the start of each processPayment() attempt.
  _lastPaymentFailure = null;

  // Configuration. Test environments can compress these by setting
  // `kiosk_developer_fastStripeTimers=true` in localStorage before the
  // service loads — same pattern as `kiosk_developer_forceKioskMode`.
  // The card window is the wall-clock time from the send-to-reader response
  // to the end of the status poll.
  STRIPE_PAYMENT_TIMEOUT_MS = 120000;
  STRIPE_PAYMENT_CHECK_INTERVAL = 3000; // 3 seconds between status checks
  STRIPE_PAYMENT_RETRY_COUNT = 3;
  STRIPE_PAYMENT_RETRY_DELAY = 1000;

  DEFAULT_VOUCHER_CODE = '5678';

  get voucherPaymentCode() {
    // Allow kiosk config to override default voucher code
    return store.kiosk?.voucher_code || this.DEFAULT_VOUCHER_CODE;
  }

  // Legacy getter for backwards compatibility
  get VOUCHER_PAYMENT_CODE() {
    return this.voucherPaymentCode;
  }

  get paymentStepLink() {
    // GoPhoto kiosks skip payment and go directly to upload
    if (store.kiosk?.gophoto_printing) {
      return 'gophoto-upload';
    }

    return this.isStripePayment ? 'payment-type' : 'printing';
  }

  /** @param {{ clock?: PaymentClock }} [options] */
  constructor({ clock = SYSTEM_CLOCK } = {}) {
    /** @type {PaymentClock} */
    this.clock = clock;
    exposeToGlobalScope(this, 'Payment');
    this._applyDevTimerOverrides();
  }

  /**
   * Dev/test hook: compress Stripe timings through the
   * `kiosk_developer_fastStripeTimers` localStorage flag.
   *
   * - `'true'`: a 500 ms card window polled every 10 ms, for mocked backends.
   * - a positive integer: a card window of that many ms, polled every 500 ms,
   *   for a live reader that needs a few seconds to present a card.
   */
  _applyDevTimerOverrides() {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      const knob = localStorage.getItem('kiosk_developer_fastStripeTimers');
      if (knob === 'true') {
        this.STRIPE_PAYMENT_TIMEOUT_MS = 500;
        this.STRIPE_PAYMENT_CHECK_INTERVAL = 10;
        this.STRIPE_PAYMENT_RETRY_DELAY = 5;
        return;
      }
      const timeoutMs = Number(knob);
      if (Number.isSafeInteger(timeoutMs) && timeoutMs > 0) {
        this.STRIPE_PAYMENT_TIMEOUT_MS = timeoutMs;
        this.STRIPE_PAYMENT_CHECK_INTERVAL = 500;
        this.STRIPE_PAYMENT_RETRY_DELAY = 5;
      }
    } catch (_e) {
      // localStorage may throw in sandboxed envs — fall back to defaults.
    }
  }

  /**
   * Sanitize error messages to remove potential card numbers or sensitive data
   * @param {string} message - Error message to sanitize
   * @returns {string} - Sanitized error message
   */
  _sanitizeErrorMessage(message) {
    if (!message || typeof message !== 'string') {
      return message;
    }
    // Remove potential card numbers (13-19 consecutive digits)
    let sanitized = message.replace(/\b\d{13,19}\b/g, '[CARD_REDACTED]');
    // Remove CVV-like patterns (3-4 digits preceded by cvv/cvc/security)
    sanitized = sanitized.replace(
      /\b(cvv|cvc|security\s*code)[:\s]*\d{3,4}\b/gi,
      '$1:[REDACTED]'
    );
    // Remove expiry date patterns (MM/YY or MM/YYYY)
    sanitized = sanitized.replace(
      /\b(0[1-9]|1[0-2])\s*[/-]\s*(\d{2}|\d{4})\b/g,
      '[EXPIRY_REDACTED]'
    );
    return sanitized;
  }

  /**
   * Prepare payment data for Sentry logging.
   * @param {Object} data - Data to sanitize
   * @returns {Object} - Data with operational Stripe/reader IDs preserved
   */
  _sanitizeForSentry(data) {
    const sanitized = { ...data };

    // Do not truncate paymentIntentId, accountId, stripeAccountId, readerId,
    // idempotencyKey, or session IDs here. They are operational join keys
    // required to correlate Sentry events with Stripe dashboard records,
    // Postgres rows, backend logs, and physical terminal state.

    // Sanitize error messages
    if (sanitized.errorMessage) {
      sanitized.errorMessage = this._sanitizeErrorMessage(
        sanitized.errorMessage
      );
    }

    // Card entry data is not operational metadata and should never be useful in
    // Sentry, so keep this narrow scrub even while preserving Stripe IDs.
    sanitized.card = undefined;
    sanitized.cardNumber = undefined;
    sanitized.cvv = undefined;
    sanitized.expiry = undefined;
    sanitized.pan = undefined;

    return sanitized;
  }

  /**
   * Extract Stripe-specific error context from an error object
   * @param {PaymentError & { type?: string, decline_code?: string, param?: string }} error - Error that may be a Stripe error
   * @returns {Object} - Stripe error context fields
   */
  _getStripeErrorContext(error) {
    if (!error) {
      return {};
    }
    return {
      stripe_error_type: error.type || 'unknown',
      stripe_error_code: error.code || undefined,
      stripe_decline_code: error.decline_code || undefined,
      stripe_error_param: error.param || undefined,
    };
  }

  /**
   * Get payment context for Sentry error reporting
   * @param {Object} additionalContext - Additional context to merge
   * @returns {Object} - Safe payment context for Sentry
   */
  _getPaymentContext(additionalContext = {}) {
    return this._sanitizeForSentry({
      // Reader and terminal context
      reader_id: authConfig.getStripeReaderId(),
      terminal_connection_status: this.isPaymentInProgress ? 'active' : 'idle',
      // Payment configuration
      pricePerPortrait: this.pricePerPortrait,
      pricePerGroupPortrait: this.pricePerGroupPortrait,
      pricePerCopy: this.pricePerCopy,
      payment_currency: this.currency,
      currency: this.currency,
      isStripePayment: this.isStripePayment,
      paymentDestination: this.paymentDestination,
      stripeAccountId: this.stripeAccountId,
      // Payment state
      isPaymentInProgress: this.isPaymentInProgress,
      isAborted: this.isAborted,
      ...additionalContext,
    });
  }

  // A reader marker at boot comes from a reload during a card interaction; its
  // reset also releases the hold, which the backend never releases itself.
  initialize() {
    if (!this._pageLoadResetDone) {
      this._pageLoadResetDone = true;
      const marker = readReaderActionMarker();
      if (marker) {
        this._resetReader('page_load', marker.payment_intent_id ?? null);
      }
    }

    this.warnIfReaderMissing();

    // Track payment configuration in Sentry
    sentryService.addBreadcrumb(
      'payment',
      'Payment service initialized',
      this._sanitizeForSentry({
        pricePerPortrait: this.pricePerPortrait,
        pricePerGroupPortrait: this.pricePerGroupPortrait,
        pricePerCopy: this.pricePerCopy,
        currency: this.currency,
        isStripePayment: this.isStripePayment,
        configStripePayment: this._isStripePaymentConfigured(store.kiosk),
        readerId: authConfig.getStripeReaderId(),
        paymentDestination: this.paymentDestination,
        stripeAccountId: this.stripeAccountId,
      })
    );

    if (this.isPartnerAccountMissing(store.kiosk, store.kioskPartner)) {
      const error = new Error(
        'Partner payment mode requires Stripe Connect account'
      );
      sentryService.captureException(error, {
        tags: { payment_error: 'config_invalid' },
        extra: this._getPaymentContext({
          error_source: 'config_validation',
          missing_field: 'stripeAccountId',
        }),
        level: 'error',
      });
      useUiStore().showError('payment_partner_mode_missing_stripe_account');
      throw new Error('payment_partner_mode_missing_stripe_account');
    }
  }

  isPartnerAccountMissing(kiosk, partner) {
    return (
      this._isStripePaymentFor(kiosk) &&
      (kiosk?.payment_destination || 'platform') === 'partner' &&
      !partner?.stripe_account_id
    );
  }

  warnIfReaderMissing() {
    if (
      !this._isStripePaymentConfigured(store.kiosk) ||
      authConfig.getStripeReaderId()
    ) {
      return;
    }
    console.warn(
      '[Payment] Stripe terminal payment enabled in config but no reader_id configured — disabling Stripe payment'
    );
    sentryService.addBreadcrumb(
      'payment',
      'Stripe payment disabled: no reader_id configured',
      { config_isStripePayment: true },
      'warning'
    );
    // Each config refresh calls this again: one Sentry event per kiosk per
    // page load is enough for ops, and an event for each call floods the issue.
    const slug = store.kiosk?.slug ?? null;
    const kioskKey = slug ?? store.kioskId ?? 'unknown-kiosk';
    if (Payment._warnedReaderlessKiosks.has(kioskKey)) {
      return;
    }
    Payment._warnedReaderlessKiosks.add(kioskKey);
    sentryService.captureMessage(
      'Stripe terminal payment enabled but no reader_id configured',
      {
        tags: {
          payment_error: 'missing_reader_id',
          severity: 'config',
          kiosk_slug: slug ?? 'unknown',
        },
        extra: this._getPaymentContext({
          config_isStripePayment: true,
          kiosk_slug: slug,
          kiosk_id: store.kioskId,
          session_id: store.sessionId,
          gophoto_code: store.gophotoCode,
        }),
        level: 'warning',
      }
    );
  }

  /**
   * Reset session-specific payment state — called between sessions. The
   * prices, the currency and the Stripe settings belong to the kiosk config.
   */
  resetForNewSession() {
    const wasInProgress = this.isPaymentInProgress;

    sentryService.addBreadcrumb(
      'payment',
      'Payment service reset for new session',
      {
        wasPaymentInProgress: wasInProgress,
        hadCurrency: !!this.currency,
        paymentIntentSentToReader: this._paymentIntentSentToReader,
      }
    );

    if (wasInProgress) {
      const error = new Error(
        'Session ended while payment was in progress -- potential orphaned payment intent'
      );
      sentryService.captureException(error, {
        tags: {
          payment_error: 'session_reset_during_payment',
          payment_currency: this.currency,
          payment_destination: this.paymentDestination,
        },
        extra: this._getPaymentContext({
          error_source: 'session_reset',
          reader_id: authConfig.getStripeReaderId(),
        }),
        level: 'fatal',
      });
      sentryService.incrementCounter('payment.orphaned_intent', 1, {
        currency: this.currency || 'unknown',
        payment_destination: this.paymentDestination,
      });
    }

    // The generation changes first, thus the poll that the cleanup stops
    // ends as a stale attempt and not as a cancel of the visitor.
    this._paymentGeneration++;
    if (this._pollingCleanup) {
      this._pollingCleanup();
      this._pollingCleanup = null;
    }
    this.isPaymentInProgress = false;
    this._resumeIdleWatcher();

    // Reset the reader only if this session sent an intent to it, or if a
    // reset that the marker records did not go through. The reset releases a
    // held amount that no hold step accepted: of the intent on the reader, or
    // of a paid hold. An accepted payment clears the two intents.
    const needsReaderReset =
      this._paymentIntentSentToReader || readReaderActionMarker() !== null;
    this._paymentIntentSentToReader = false;
    this.isAborted = true;
    const cancelPromise = needsReaderReset
      ? this._resetReader(
          'session_end',
          this._readerPaymentIntentId ?? this._paidHoldIntentId
        )
      : (this._cancelInFlight ?? Promise.resolve());

    // Clear session-specific intent state
    this._lastCaptureReadyIntentId = null;
    this._lastCaptureReadyAccountId = null;
    this._lastAttemptParams = null;
    this._checkoutKey = null;
    this._readerPaymentIntentId = null;
    this._paidHoldIntentId = null;
    this.failedPaymentCount = 0;

    try {
      sentryService.setContext('payment', null);
    } catch (e) {
      console.warn('[Payment] Sentry context clear failed:', e.message);
    }

    return cancelPromise;
  }

  reset() {
    return this.resetForNewSession();
  }

  getTotalSum(printingQueue) {
    // Handle invalid input gracefully
    if (!Array.isArray(printingQueue)) {
      console.warn(
        '[Payment] getTotalSum called with non-array:',
        typeof printingQueue
      );
      return 0;
    }

    // Count total copies across all portraits
    let totalCopies = 0;
    let hasMultiPortrait = false;

    for (const portrait of printingQueue) {
      totalCopies += portrait.count;
      if (portrait.isGroupPortrait) {
        hasMultiPortrait = true;
      }
    }

    if (totalCopies === 0) {
      return 0;
    }

    // First copy: use price_multi if multi portrait, otherwise price
    const firstCopyPrice = hasMultiPortrait
      ? this.pricePerGroupPortrait
      : this.pricePerPortrait;

    // Additional copies: use price_multi_copy discount
    // Round to 2 decimal places to avoid floating point precision issues (e.g., 3.33 + 3.33 = 6.659999999999999)
    const total =
      firstCopyPrice + Math.max(0, totalCopies - 1) * this.pricePerCopy;
    return Math.round(total * 100) / 100;
  }

  getTotalSumInCents(printingQueue) {
    // Currency-aware minor units — a blind * 100 over-charges zero-decimal
    // currencies (JPY/CLP) 100×. Mirrors backend/utils/currency.py.
    return toMinorUnits(this.getTotalSum(printingQueue), this.currency);
  }

  /**
   * @param {Array} printingQueue - The portraits of the order.
   * @param {number | null} [amountCents] - The amount of a reprint order, which
   *   replaces the price of the queue.
   */
  getTotalSumFormatted(printingQueue, amountCents = null) {
    const effectiveCurrency = this.currency || 'eur';
    const sum = Number.isInteger(amountCents)
      ? fromMinorUnits(amountCents, effectiveCurrency)
      : this.getTotalSum(printingQueue);
    return formatSum(sum, effectiveCurrency);
  }

  /**
   * The amount of each copy of an order, in minor units, in print order.
   * The copies of one payment add up to its amount; see allocateCopyAmounts.
   */
  getCopyAmountsInCents(printingQueue, totalCents) {
    return allocateCopyAmounts(
      printingQueue,
      totalCents,
      toMinorUnits(this.pricePerCopy, this.currency)
    );
  }

  /**
   * The copies that the kiosk declares at create, or null. The backend
   * accepts a declaration only when it has one amount for each copy and the
   * amounts add up to the payment. Without a declaration, the kiosk captures
   * at the payment. O(n) in the copies.
   *
   * @param {unknown} copyAmountsCents - one amount for each copy that the
   *   kiosk submits with this payment, in print order
   * @param {number} amount - the amount of the payment, in minor units
   * @returns {number[] | null}
   */
  _orderDeclaration(copyAmountsCents, amount) {
    if (
      !Array.isArray(copyAmountsCents) ||
      copyAmountsCents.length === 0 ||
      copyAmountsCents.length > MAX_DECLARED_COPIES ||
      !copyAmountsCents.every((value) => Number.isInteger(value) && value >= 0)
    ) {
      return null;
    }
    const sum = copyAmountsCents.reduce((total, value) => total + value, 0);
    return sum === amount ? [...copyAmountsCents] : null;
  }

  /**
   * True when an earlier payment intent pays the current order: the same
   * amount and the same declared copies. The copies of the order bind the
   * intent with their amounts, thus a different order must not use it. An
   * intent without a declaration captures at the payment (an older backend
   * ignores the declaration): then the amount alone decides.
   *
   * @param {{ amount?: number, metadata?: Record<string, string> | null }} paymentIntent
   * @param {number} amount - the amount of the current order, in minor units
   * @param {number[] | null} declaredCopies - see _orderDeclaration
   * @returns {boolean}
   */
  _isSameOrder(paymentIntent, amount, declaredCopies) {
    const declared = declarationOf(paymentIntent);
    return (
      paymentIntent?.amount === amount &&
      (declared === null || isSameDeclaration(declared, declaredCopies))
    );
  }

  /**
   * Release the held amount of an earlier intent that the current order does
   * not use. The backend releases only a hold that the kiosk did not accept,
   * and it cannot release a captured payment. When this call fails, the hold
   * sweep of the backend releases the hold later.
   */
  async _releaseChangedOrder(paymentIntentId, accountId, readerId) {
    sentryService.addBreadcrumb(
      'payment',
      'Release the previous intent of a changed order',
      { prevIntentId: paymentIntentId },
      'warning'
    );
    try {
      await restApi.cancelPaymentOnReader(readerId, accountId, {
        sessionId: store.sessionId ?? undefined,
        paymentIntentId,
        releaseHold: true,
      });
    } catch (error) {
      sentryService.addBreadcrumb(
        'payment',
        'Release of the previous intent failed',
        this._sanitizeForSentry({
          prevIntentId: paymentIntentId,
          errorMessage: error?.message,
        }),
        'warning'
      );
    }
  }

  /**
   * @param {Array} printingQueue - The portraits of the order.
   * @param {{ amountCents?: number, copyAmountsCents?: number[] | null }} [options]
   *   amountCents overrides the price of the queue: a reprint order pays
   *   again what its failed copies cost, not the price list of the full
   *   queue. copyAmountsCents declares each copy that the kiosk submits with
   *   this payment: then the reader holds the amount, and the backend
   *   captures only the printed copies.
   */
  processPayment(printingQueue, options = {}) {
    // Prevent concurrent payment attempts
    if (this.isPaymentInProgress) {
      console.warn(
        '[Payment] Payment already in progress, ignoring duplicate request'
      );
      sentryService.addBreadcrumb(
        'payment',
        'Duplicate payment request blocked',
        {},
        'warning'
      );
      return Promise.reject(new Error('Payment already in progress'));
    }

    // Validate printingQueue is not empty
    if (!Array.isArray(printingQueue) || printingQueue.length === 0) {
      console.error(
        '[Payment] Cannot process payment: printing queue is empty',
        {
          printingQueue,
          pricePerPortrait: this.pricePerPortrait,
          currency: this.currency,
        }
      );
      const error = new Error('No items in printing queue');
      sentryService.captureException(error, {
        tags: { payment_error: 'empty_queue' },
        extra: this._getPaymentContext({
          error_source: 'queue_validation',
          queueLength: printingQueue?.length || 0,
          queueIsArray: Array.isArray(printingQueue),
        }),
        level: 'warning',
      });
      return Promise.reject(error);
    }

    // Reset state for new payment attempt
    this.isAborted = false;
    this.isPaymentInProgress = true;

    // Bump generation so any lingering stale poll callbacks from a previous
    // payment attempt are detected and discarded.
    this._paymentGeneration++;

    // Track payment initiation for success rate calculation
    sentryService.incrementCounter('payment.initiated', 1, {
      currency: this.currency,
      payment_destination: this.paymentDestination,
    });

    this._pauseIdleWatcher();

    const amount = Number.isInteger(options?.amountCents)
      ? options.amountCents
      : this.getTotalSumInCents(printingQueue);
    const declaredCopies = this._orderDeclaration(
      options?.copyAmountsCents,
      amount
    );
    const totalCopies = printingQueue.reduce((acc, p) => acc + p.count, 0);
    const paymentStartTime = Date.now();
    this._checkoutKey ??= `b2c-checkout-${uuidv4()}`;
    let checkoutKey = this._checkoutKey;

    // Generate idempotency key for this payment attempt.
    // If the network retries the createPaymentIntent call, the backend uses this
    // key to deduplicate, preventing double charges from the same user action.
    // Reassigned (not const) when a retry with identical params can reuse the
    // previous attempt's key -- see the Step-0 status-check failure path.
    let idempotencyKey = uuidv4();

    // Track payment initiated breadcrumb
    clientEvents.record(
      'payment_started',
      {
        amount_cents: amount,
        currency: this.currency,
        total_copies: totalCopies,
        queue_length: printingQueue.length,
        payment_destination: this.paymentDestination,
      },
      { area: 'payment' }
    );
    sentryService.addBreadcrumb(
      'payment',
      'Payment initiated',
      this._sanitizeForSentry({
        amountCents: amount,
        currency: this.currency,
        totalCopies,
        queueLength: printingQueue.length,
        // Preserve full idempotency keys so duplicate-charge investigations can
        // match frontend retries to backend and Stripe requests.
        idempotencyKey,
      })
    );

    // Validate currency is configured before sending payment
    if (!this.currency || typeof this.currency !== 'string') {
      this._finishPayment();
      const errorMsg = 'Payment cannot proceed: currency is not configured';
      console.error('[Payment] Currency validation failed:', errorMsg);
      const error = new Error(errorMsg);
      sentryService.captureException(error, {
        tags: { payment_error: 'currency_missing' },
        extra: this._getPaymentContext({
          error_source: 'currency_validation',
          currency_value: this.currency,
          currency_type: typeof this.currency,
        }),
        level: 'error',
      });
      return Promise.reject(error);
    }

    // Validate amount is a safe integer (not NaN, Infinity, negative, or fractional)
    if (
      !Number.isFinite(amount) ||
      amount < 0 ||
      amount !== Math.floor(amount)
    ) {
      this._finishPayment();
      const errorMsg = `Payment amount invalid: ${amount} (type: ${typeof amount})`;
      console.error('[Payment] Amount validation failed:', errorMsg);
      const error = new Error(errorMsg);
      sentryService.captureException(error, {
        tags: { payment_error: 'amount_invalid' },
        extra: this._getPaymentContext({
          error_source: 'amount_validation',
          payment_amount: amount,
          amountType: typeof amount,
          isNaN: Number.isNaN(amount),
          isFinite: Number.isFinite(amount),
          pricePerPortrait: this.pricePerPortrait,
          pricePerCopy: this.pricePerCopy,
        }),
        level: 'error',
      });
      return Promise.reject(error);
    }

    // Validate minimum amount (Stripe requires at least 50 cents)
    if (amount < 50) {
      this._finishPayment();
      const errorMsg =
        this.pricePerPortrait === 0
          ? 'Payment cannot proceed: price is not configured (0)'
          : `Payment amount (${amount} cents) is below Stripe minimum (50 cents)`;
      console.error('[Payment] Amount validation failed:', errorMsg);
      const error = new Error(errorMsg);
      sentryService.captureException(error, {
        tags: { payment_error: 'amount_too_low' },
        extra: this._getPaymentContext({
          error_source: 'amount_validation',
          payment_amount: amount,
          amountCents: amount,
          minRequired: 50,
          totalCopies,
          queueLength: printingQueue.length,
          priceConfigured: this.pricePerPortrait !== 0,
        }),
        level: 'error',
      });
      return Promise.reject(error);
    }

    // Only pass stripeAccountId for partner mode (Stripe Connect)
    // For platform mode, pass null so payment goes to platform's own account
    const accountId =
      this.paymentDestination === 'partner' ? this.stripeAccountId : null;

    // Get reader ID from URL/localStorage config (allows per-kiosk reader configuration).
    // CRITICAL: Without a reader_id, we cannot send the PaymentIntent to any physical
    // terminal. Reject immediately to avoid creating orphaned intents on Stripe.
    const readerId = authConfig.getStripeReaderId();
    if (!readerId) {
      this._finishPayment();
      const error = new Error(
        'Cannot process payment: no Stripe Terminal reader configured (missing reader_id URL parameter)'
      );
      console.error('[Payment]', error.message);
      sentryService.captureException(error, {
        tags: { payment_error: 'missing_reader_id' },
        extra: this._getPaymentContext({
          error_source: 'reader_validation',
        }),
        level: 'error',
      });
      return Promise.reject(error);
    }

    // Wrap entire payment flow in a Sentry span for transaction tracking
    const attempt = sentryService.withSpan(
      'payment.process',
      'payment',
      async () => {
        let paymentIntentId = null;
        /** @type {{ id?: string, status?: string, outcome?: string, payment_method?: { type?: string }, failure?: { category?: string, code?: string, decline_code?: string, message?: string } | null } | null} */
        let lastKnownPaymentIntent = null;
        // Reset per attempt: the last differentiated failure the poll observed.
        this._lastPaymentFailure = null;
        const attemptGeneration = this._paymentGeneration;
        let isSentToReader = false;

        try {
          // Step -1: Wait for any in-flight cancel-reader API call to complete.
          // Without this, a previous cancel (from timeout or user abort) could
          // arrive at Stripe AFTER our new sendPaymentIntentToReader call,
          // killing the new payment on the reader. cancelPaymentOnReader is
          // reader-level, not intent-level -- it cancels whatever the reader
          // is currently doing.
          while (this._cancelInFlight) {
            sentryService.addBreadcrumb(
              'payment',
              'Waiting for in-flight cancel to settle'
            );
            await this._cancelInFlight;
          }
          // A cancel or the session end during the wait ends the attempt. A
          // create after the session end has no session, and the intent
          // that it makes stays on Stripe.
          const leftDuringReset = this._visitorLeftError(
            attemptGeneration,
            'the reader reset'
          );
          if (leftDuringReset) {
            throw leftDuringReset;
          }

          // Step 0: Check if a previous capture-ready intent was already captured.
          // This prevents double charges when: capture succeeded on Stripe but the
          // response was lost (network error), causing the UI to show "failed" and
          // the user to retry. Without this check, retry would create a second charge.
          // The kiosk uses that intent only for the same order: the same
          // amount and the same declared copies (see _isSameOrder).
          if (this._lastCaptureReadyIntentId) {
            const prevIntentId = this._lastCaptureReadyIntentId;
            const prevAccountId = this._lastCaptureReadyAccountId;
            this._lastCaptureReadyIntentId = null;
            this._lastCaptureReadyAccountId = null;

            sentryService.addBreadcrumb(
              'payment',
              'Checking previous intent before retry',
              {
                prevIntentId,
              }
            );

            let prevIntent = null;
            try {
              prevIntent = await restApi.checkPaymentIntentStatus(
                prevIntentId,
                prevAccountId
              );
            } catch (prevCheckError) {
              // Could not check previous intent (network down) -- proceed anyway.
              // If the params are unchanged, reuse the previous attempt's
              // idempotency key so Stripe returns the SAME intent instead of
              // minting a second one (a second PI is invisible to the refund
              // path, which anchors on the session's stored intent id).
              // Changed params MUST get a fresh key -- same key + different
              // params is a hard Stripe idempotency_error. The declared copies
              // go into the metadata of the intent, thus they are params too.
              const prevAttempt = this._lastAttemptParams;
              const canReuseKey =
                prevAttempt &&
                prevAttempt.amount === amount &&
                prevAttempt.currency === this.currency &&
                prevAttempt.accountId === accountId &&
                isSameDeclaration(
                  prevAttempt.copyAmountsCents ?? null,
                  declaredCopies
                );
              if (canReuseKey) {
                idempotencyKey = prevAttempt.idempotencyKey;
              }
              sentryService.addBreadcrumb(
                'payment',
                'Previous intent check failed, proceeding with new',
                {
                  error: prevCheckError.message?.substring(0, 100),
                  reused_idempotency_key: Boolean(canReuseKey),
                },
                'warning'
              );
            }

            const prevIsReady =
              prevIntent?.status === 'succeeded' ||
              prevIntent?.status === 'requires_capture';
            if (
              prevIsReady &&
              !this._isSameOrder(prevIntent, amount, declaredCopies)
            ) {
              // The queue changed after the earlier card payment. That
              // intent pays a different order, thus the kiosk releases it
              // and starts a new order with a new payment.
              this._paidHoldIntentId = null;
              await this._releaseChangedOrder(
                prevIntentId,
                prevAccountId,
                readerId
              );
              this._checkoutKey = `b2c-checkout-${uuidv4()}`;
              checkoutKey = this._checkoutKey;
            } else if (prevIntent?.status === 'succeeded') {
              // Previous capture DID succeed -- return it instead of creating a new charge
              sentryService.addBreadcrumb(
                'payment',
                'Previous intent already captured, reusing',
                {
                  prevIntentId,
                }
              );
              sentryService.incrementCounter(
                'payment.double_charge_prevented',
                1,
                {
                  currency: this.currency,
                }
              );
              this._lastAttemptParams = null;
              this._checkoutKey = null;
              this._readerPaymentIntentId = null;
              this._paidHoldIntentId = null;
              this._finishPayment();
              return prevIntent;
            } else if (prevIntent?.status === 'requires_capture') {
              // Accept it instead of creating new: the hold step for a
              // declared order, else the capture.
              const holdPrevious = prevIntent.capture === CAPTURE_AFTER_PRINT;
              if (holdPrevious) {
                this._paidHoldIntentId = prevIntentId;
              }
              sentryService.addBreadcrumb(
                'payment',
                holdPrevious
                  ? 'Accept the previous held intent'
                  : 'Capturing previous uncaptured intent',
                {
                  prevIntentId,
                }
              );
              let captured;
              try {
                captured = await this._retryOnNetworkError(
                  () =>
                    holdPrevious
                      ? restApi.holdPaymentIntent(
                          prevIntentId,
                          prevAccountId,
                          checkoutKey
                        )
                      : restApi.capturePaymentIntent(
                          prevIntentId,
                          prevAccountId,
                          checkoutKey
                        ),
                  // The HTTP client retries /hold (RETRYABLE_ENDPOINTS_BY_METHOD).
                  holdPrevious ? 1 : this.STRIPE_PAYMENT_RETRY_COUNT,
                  holdPrevious
                    ? 'holdPaymentIntent_retry_prev'
                    : 'capturePaymentIntent_retry_prev'
                );
              } catch (acceptError) {
                // The card paid this intent. A new intent charges the card a
                // second time, and the failure reset of the new intent can
                // release this one. Thus the attempt stops, and the next try
                // accepts this intent again.
                this._lastCaptureReadyIntentId = prevIntentId;
                this._lastCaptureReadyAccountId = prevAccountId;
                throw this._actionFailureError(acceptError);
              }
              const leftDuringHold = holdPrevious
                ? this._visitorLeftError(attemptGeneration)
                : null;
              if (leftDuringHold) {
                if (leftDuringHold.code === Payment.ERROR_USER_CANCELLED) {
                  // The visitor can pay this order again in this session.
                  this._lastCaptureReadyIntentId = prevIntentId;
                  this._lastCaptureReadyAccountId = prevAccountId;
                }
                throw leftDuringHold;
              }
              if (isPaymentAccepted(captured)) {
                sentryService.incrementCounter(
                  'payment.double_charge_prevented',
                  1,
                  {
                    currency: this.currency,
                  }
                );
                this._lastAttemptParams = null;
                this._checkoutKey = null;
                this._readerPaymentIntentId = null;
                this._paidHoldIntentId = null;
                this._finishPayment();
                return captured;
              }
            }
            // Otherwise (cancelled, failed, etc.) -- proceed with new intent
          }

          // Step 1: Create payment intent
          sentryService.addBreadcrumb('payment', 'Creating payment intent', {
            amountCents: amount,
            currency: this.currency,
          });

          // Recorded BEFORE the call: a create whose response is lost may
          // still have made the intent under this key on Stripe's side.
          this._lastAttemptParams = {
            idempotencyKey,
            amount,
            currency: this.currency,
            accountId,
            copyAmountsCents: declaredCopies,
          };

          const createdIntent = await sentryService.withSpan(
            'payment.create_intent',
            'payment.stripe',
            () =>
              this._retryOnNetworkError(
                () =>
                  restApi.createPaymentIntent(
                    amount,
                    this.currency,
                    accountId,
                    idempotencyKey,
                    checkoutKey,
                    declaredCopies
                  ),
                this.STRIPE_PAYMENT_RETRY_COUNT,
                'createPaymentIntent'
              ),
            {
              'payment.amount_cents': amount,
              'payment.currency': this.currency,
              'payment.idempotency_key': idempotencyKey,
              'payment.declared_copies': declaredCopies?.length ?? 0,
            }
          );

          paymentIntentId = createdIntent.id;
          lastKnownPaymentIntent = createdIntent;
          // A missing field is an older backend: it captures at the payment.
          const captureAfterPrint =
            createdIntent?.capture === CAPTURE_AFTER_PRINT;
          clientEvents.record(
            'payment_intent_created',
            {
              payment_intent_id: paymentIntentId,
              status: createdIntent.status,
              amount_cents: amount,
              currency: this.currency,
              capture: createdIntent?.capture ?? 'at_payment',
              declared_copies: declaredCopies?.length ?? 0,
            },
            { area: 'payment' }
          );

          sentryService.addBreadcrumb(
            'payment',
            'Payment intent created',
            this._sanitizeForSentry({
              paymentIntentId,
              status: createdIntent.status,
            })
          );

          const leftDuringCreate = this._visitorLeftError(
            attemptGeneration,
            'the create of the intent'
          );
          if (leftDuringCreate) {
            throw leftDuringCreate;
          }

          // Step 2: Send to terminal reader
          sentryService.addBreadcrumb(
            'payment',
            'Sending to terminal reader',
            this._sanitizeForSentry({
              paymentIntentId,
              readerId,
            })
          );

          // Set BEFORE the await: even if the request fails mid-flight, the
          // reader might have received the PI, so the next reset should still
          // attempt cancel (best-effort cleanup). False positive cost is one
          // unnecessary cancel API call; false negative cost is an orphaned
          // PI on the reader.
          this._paymentIntentSentToReader = true;
          isSentToReader = true;
          this._readerPaymentIntentId = createdIntent.id;
          writeReaderActionMarker({
            payment_intent_id: createdIntent.id,
            reader_id: readerId,
            account_id: accountId,
          });
          clientEvents.record(
            'payment_sent_to_reader',
            {
              payment_intent_id: createdIntent.id,
              reader_id: readerId,
            },
            { area: 'payment' }
          );

          const send = sentryService.withSpan(
            'payment.send_to_reader',
            'payment.stripe',
            () =>
              this._retryOnNetworkError(
                () =>
                  restApi.sendPaymentIntentToReader(
                    createdIntent.id,
                    accountId,
                    readerId
                  ),
                this.STRIPE_PAYMENT_RETRY_COUNT,
                'sendPaymentIntentToReader'
              ),
            { 'payment.reader_id': readerId }
          );
          this._watchReaderSend(createdIntent.id, send);
          let sentToReader;
          try {
            sentToReader = await send;
          } catch (sendError) {
            const refusal = this._actionFailureError(sendError);
            // _resetReader also skips an offline refusal, but only while
            // _readerSend still names this send. This flag does not depend on
            // that timing. The reset of a cancel during the send needs the
            // guard in _resetReader.
            isSentToReader = !isReaderOffline(refusal?.failure);
            throw refusal;
          }
          lastKnownPaymentIntent = sentToReader;
          const cardDeadlineAt =
            this.clock.now() + this.STRIPE_PAYMENT_TIMEOUT_MS;

          // The status poll does not read the reader action, thus it cannot
          // see a reader fault. Stop here. A backend of a previous release
          // gives the reader fault in a 200 response. Stop only for
          // reader_error: an intent that a retry uses again can carry the
          // card decline of a previous tap.
          if (
            sentToReader?.outcome === 'failed' &&
            sentToReader.failure?.category === 'reader_error'
          ) {
            isSentToReader = !isReaderOffline(sentToReader.failure);
            throw this._failureError(sentToReader.failure);
          }

          sentryService.addBreadcrumb(
            'payment',
            'Waiting for card presentation',
            this._sanitizeForSentry({
              paymentIntentId: sentToReader.id,
            })
          );

          const leftDuringSend = this._visitorLeftError(
            attemptGeneration,
            'the send to the reader'
          );
          if (leftDuringSend) {
            throw leftDuringSend;
          }

          // Step 3: Wait for card presentation and authorization
          const readyForCapture = await sentryService.withSpan(
            'payment.wait_for_capture',
            'payment.terminal',
            () =>
              this.waitForPaymentIntentReadyForCapture(
                sentToReader.id,
                accountId,
                // A 200 send answers `pending` with the failure of the previous
                // tap on an intent that a retry sends again.
                sentToReader?.failure ?? null,
                cardDeadlineAt
              ),
            { 'payment.intent_id': paymentIntentId }
          );
          lastKnownPaymentIntent = readyForCapture;

          // Save the capture-ready intent ID so that on retry (after capture failure)
          // we can check if the previous capture actually went through on Stripe's side,
          // preventing double charges from lost network responses.
          this._lastCaptureReadyIntentId = readyForCapture.id;
          this._lastCaptureReadyAccountId = accountId;
          // The card paid. A retry accepts this payment also after a failed
          // hold step, thus the reset at the end of a failed payment does not
          // release it. Until a hold step accepts it, a cancel or the session
          // end releases it.
          this._readerPaymentIntentId = null;
          this._paidHoldIntentId = captureAfterPrint
            ? readyForCapture.id
            : null;
          clientEvents.record(
            'payment_ready_for_capture',
            {
              payment_intent_id: readyForCapture.id,
              status: readyForCapture.status,
            },
            { area: 'payment' }
          );

          sentryService.addBreadcrumb(
            'payment',
            'Card authorized, capturing payment',
            this._sanitizeForSentry({
              paymentIntentId: readyForCapture.id,
              status: readyForCapture.status,
            })
          );

          const leftDuringPoll = this._visitorLeftError(
            attemptGeneration,
            'the status poll'
          );
          if (leftDuringPoll) {
            throw leftDuringPoll;
          }

          // Step 4: Accept the payment. A declared order goes to the hold
          // step: the backend captures only the printed copies after the
          // print. Else the kiosk captures the full amount here.
          // CRITICAL: At this point the card IS authorized (money held). If
          // the call fails because of a short network error and the kiosk
          // does not try again, the visitor sees "payment failed" while the
          // card has a pending hold. A new payment intent can then charge
          // the card two times. Thus the kiosk tries again with a longer
          // delay each time before it stops.
          let capturedPayment;
          try {
            capturedPayment = captureAfterPrint
              ? await sentryService.withSpan(
                  'payment.hold',
                  'payment.stripe',
                  () =>
                    this._retryOnNetworkError(
                      () =>
                        restApi.holdPaymentIntent(
                          readyForCapture.id,
                          accountId,
                          checkoutKey
                        ),
                      // The HTTP client retries /hold (RETRYABLE_ENDPOINTS_BY_METHOD).
                      1,
                      'holdPaymentIntent'
                    ),
                  { 'payment.intent_id': paymentIntentId }
                )
              : await sentryService.withSpan(
                  'payment.capture',
                  'payment.stripe',
                  () =>
                    this._retryOnNetworkError(
                      () =>
                        restApi.capturePaymentIntent(
                          readyForCapture.id,
                          accountId,
                          checkoutKey
                        ),
                      this.STRIPE_PAYMENT_RETRY_COUNT,
                      'capturePaymentIntent'
                    ),
                  { 'payment.intent_id': paymentIntentId }
                );
          } catch (acceptError) {
            throw this._actionFailureError(acceptError);
          }
          lastKnownPaymentIntent = capturedPayment;
          // A hold step that answers after the visitor went away does not
          // print. The saved intent stays, thus a next payment of the same
          // order in this session examines it again.
          const leftError = captureAfterPrint
            ? this._visitorLeftError(attemptGeneration)
            : null;
          if (leftError) {
            throw leftError;
          }

          // The kiosk accepted the payment. Clear the saved intent: a retry
          // must not examine it again. Clear the attempt params: a NEXT
          // purchase, also with the same amount, must not use this key again.
          this._lastCaptureReadyIntentId = null;
          this._lastCaptureReadyAccountId = null;
          this._lastAttemptParams = null;
          this._checkoutKey = null;
          this._paidHoldIntentId = null;
          const captureMode = captureAfterPrint
            ? CAPTURE_AFTER_PRINT
            : 'at_payment';

          // Track successful payment metrics
          const durationMs = Date.now() - paymentStartTime;
          sentryService.trackMetric(
            'payment.duration_ms',
            durationMs,
            'millisecond',
            {
              currency: this.currency,
              payment_destination: this.paymentDestination,
            }
          );
          sentryService.trackMetric('payment.amount_cents', amount, 'none', {
            currency: this.currency,
            payment_destination: this.paymentDestination,
          });
          sentryService.incrementCounter('payment.success', 1, {
            currency: this.currency,
            payment_destination: this.paymentDestination,
            capture: captureMode,
          });

          sentryService.addBreadcrumb(
            'payment',
            captureAfterPrint
              ? 'Payment held, the backend captures the printed copies'
              : 'Payment captured successfully',
            this._sanitizeForSentry({
              paymentIntentId: capturedPayment.id,
              status: capturedPayment.status,
              durationMs,
            })
          );
          // The event keeps its name for a held payment too: the diagnostics
          // panel counts the accepted payments with it.
          clientEvents.record(
            'payment_captured',
            {
              payment_intent_id: capturedPayment.id,
              status: capturedPayment.status,
              duration_ms: durationMs,
              amount_cents: amount,
              currency: this.currency,
              capture: captureMode,
            },
            { area: 'payment' }
          );

          this._resetReader('success');
          this._finishPayment();
          return capturedPayment;
        } catch (/** @type {unknown} */ e) {
          /** @type {PaymentError} */
          const error = /** @type {PaymentError} */ (e);
          this._finishPayment();

          // Track payment failure metrics
          const durationMs = Date.now() - paymentStartTime;
          sentryService.incrementCounter('payment.failure', 1, {
            currency: this.currency,
            payment_destination: this.paymentDestination,
            error_code: error.code || 'unknown',
          });

          // Track failed payment amount for analysis
          sentryService.trackMetric(
            'payment.failed_amount_cents',
            amount,
            'none',
            {
              currency: this.currency,
              error_code: error.code || 'unknown',
              payment_destination: this.paymentDestination,
            }
          );

          // Determine error category for tagging
          const errorCategory = this._categorizePaymentError(error);

          // A stale attempt does not own the reader. A cancel and a session
          // reset send their own reader reset. The kiosk did not accept this
          // payment, thus the reset also releases a hold that the reader
          // took in the last second of the card window.
          if (
            isSentToReader &&
            !this.isAborted &&
            this._paymentGeneration === attemptGeneration
          ) {
            this._resetReader(errorCategory, this._readerPaymentIntentId);
          }

          sentryService.addBreadcrumb(
            'payment',
            'Payment failed',
            this._sanitizeForSentry({
              paymentIntentId,
              errorMessage: error.message,
              errorCode: error.code,
              errorCategory,
              durationMs,
            }),
            'error'
          );
          let clientEventType = 'payment_failed';
          if (errorCategory === 'timeout') {
            clientEventType = 'payment_timeout';
          } else if (
            errorCategory === 'user_cancelled' ||
            errorCategory === 'terminal_cancelled'
          ) {
            clientEventType = 'payment_cancelled';
          }

          let clientEventLevel = 'error';
          if (
            errorCategory === 'user_cancelled' ||
            errorCategory === 'terminal_cancelled'
          ) {
            clientEventLevel = 'info';
          } else if (errorCategory === 'timeout') {
            clientEventLevel = 'warning';
          }
          clientEvents.record(
            clientEventType,
            {
              payment_intent_id: paymentIntentId,
              error_code: error.code,
              error_category: errorCategory,
              error_message: this._sanitizeErrorMessage(error.message),
              duration_ms: durationMs,
            },
            {
              area: 'payment',
              level: clientEventLevel,
            }
          );

          // Only capture actionable errors as exceptions -- skip user-initiated
          // cancellations, terminal cancellations, and stale generation rejections
          // (which occur when reset() or a new processPayment() invalidates a poll)
          if (
            errorCategory !== 'user_cancelled' &&
            errorCategory !== 'terminal_cancelled' &&
            errorCategory !== 'generation_stale'
          ) {
            // Determine Sentry level based on error severity:
            // - timeout/network/reader_unavailable/processing_error: warning (transient, often auto-recovers)
            // - card_declined/insufficient_funds/auth: info (user error, not system bug)
            // - everything else: error (system issue requiring investigation)
            const sentryLevel =
              errorCategory === 'timeout' ||
              errorCategory === 'network_error' ||
              errorCategory === 'reader_unavailable' ||
              errorCategory === 'processing_error'
                ? 'warning'
                : errorCategory === 'card_declined' ||
                    errorCategory === 'insufficient_funds' ||
                    errorCategory === 'authentication_required'
                  ? 'info'
                  : 'error';

            sentryService.captureException(error, {
              tags: {
                payment_error: errorCategory,
                payment_currency: this.currency,
                payment_destination: this.paymentDestination,
              },
              extra: this._getPaymentContext({
                // Payment intent context
                payment_intent_id: paymentIntentId,
                paymentIntentId,
                // Idempotency and terminal context (critical for financial debugging)
                idempotency_key: idempotencyKey,
                reader_id: readerId,
                // Transaction context
                payment_amount: amount,
                amountCents: amount,
                totalCopies,
                durationMs,
                // Error context from Stripe error object
                ...this._getStripeErrorContext(error),
                errorCode: error.code,
                errorMessage: this._sanitizeErrorMessage(error.message),
                // Stripe-specific context from payment intent
                payment_method_type:
                  lastKnownPaymentIntent?.payment_method?.type,
                payment_intent_status: lastKnownPaymentIntent?.status,
                payment_outcome: lastKnownPaymentIntent?.outcome,
                // `_lastPaymentFailure` holds the failure from the status poll
                // and the reader failure from send-to-reader.
                // `lastKnownPaymentIntent` does not get the declined intent
                // from the poll.
                failure: this._lastPaymentFailure
                  ? {
                      category: this._lastPaymentFailure.category,
                      code: this._lastPaymentFailure.code,
                      decline_code: this._lastPaymentFailure.decline_code,
                      message: this._sanitizeErrorMessage(
                        this._lastPaymentFailure.message
                      )?.slice(0, 200),
                    }
                  : null,
              }),
              level: sentryLevel,
            });
          }

          throw error;
        }
      },
      {
        'payment.amount_cents': amount,
        'payment.currency': this.currency,
        'payment.destination': this.paymentDestination,
        'payment.total_copies': totalCopies,
      }
    );
    this._attemptEnd = attempt.then(
      () => undefined,
      () => undefined
    );
    return attempt;
  }

  /**
   * Settles when the payment attempt in progress ends. A cancel ends the card
   * screen at once, but its attempt can wait for a slow send to the reader.
   * A card screen that opens in this time starts its payment after this end.
   *
   * @returns {Promise<void>}
   */
  whenIdle() {
    return this.isPaymentInProgress && this._attemptEnd
      ? this._attemptEnd
      : Promise.resolve();
  }

  /**
   * Categorize payment error for Sentry tagging
   * @param {PaymentError} error - The payment error (may include Stripe error code)
   * @returns {string} - Error category
   */
  _categorizePaymentError(error) {
    // Internal error codes (set by this Payment class)
    if (error.code === Payment.ERROR_USER_CANCELLED) return 'user_cancelled';
    if (error.code === Payment.ERROR_TERMINAL_CANCELLED)
      return 'terminal_cancelled';
    if (error.code === Payment.ERROR_TIMEOUT) return 'timeout';
    if (error.code === Payment.ERROR_GENERATION_STALE)
      return 'generation_stale';
    if (error.code === Payment.ERROR_READER_UNAVAILABLE) {
      return 'reader_unavailable';
    }
    if (error.code === Payment.ERROR_CARD_FAILED) {
      const failure = error.failure ?? {};
      const declineCode = failure.decline_code || failure.code || '';
      if (
        declineCode === 'insufficient_funds' ||
        declineCode === 'card_velocity_exceeded'
      ) {
        return 'insufficient_funds';
      }
      if (failure.category === 'processing_error') {
        return 'processing_error';
      }
      return 'card_declined';
    }

    // Stripe error type classification (from Stripe API error objects)
    if (error.type === 'card_error') {
      const dc = error.decline_code || error.code || '';
      if (dc === 'insufficient_funds' || dc === 'card_velocity_exceeded')
        return 'insufficient_funds';
      if (dc === 'authentication_required' || dc === 'offline_pin_required')
        return 'authentication_required';
      return 'card_declined';
    }
    if (error.type === 'invalid_request_error' || error.type === 'api_error')
      return 'system_error';
    if (error.type === 'api_connection_error') return 'network_error';

    // Network error heuristic (for non-Stripe fetch failures)
    const msg = error.message?.toLowerCase() || '';
    if (
      msg.includes('network') ||
      msg.includes('failed to fetch') ||
      msg.includes('load failed') ||
      error.name === 'TypeError'
    )
      return 'network_error';

    // Message-based fallback
    if (msg.includes('declined') || msg.includes('card'))
      return 'card_declined';

    return 'unknown';
  }

  /**
   * @param {{ category?: string, message?: string | null }} failure
   * @param {unknown} [cause]
   * @returns {PaymentError}
   */
  _failureError(failure, cause = undefined) {
    this._lastPaymentFailure = failure;
    if (failure.category === 'reader_error') {
      return paymentError(
        Payment.ERROR_READER_UNAVAILABLE,
        'Card reader is not available',
        failure,
        cause
      );
    }
    return paymentError(
      Payment.ERROR_CARD_FAILED,
      failure.message || 'Card payment failed',
      failure,
      cause
    );
  }

  // The v1 action routes answer a failed action with an HTTP error, and the
  // endpoint puts the failure of the body on the error. Thus the visitor sees
  // the same cause as for a failure in the status poll.
  _actionFailureError(error) {
    return error?.failure ? this._failureError(error.failure, error) : error;
  }

  /**
   * The error for a step of the attempt that answered after the visitor went
   * away, or null. The cancel or the session end sent the reader reset and the
   * release of a hold. If a hold step came to the backend first, the backend
   * keeps the hold, and its sweep cancels it because no copy printed. The
   * visitor went away, thus the kiosk does not print for this payment.
   *
   * @param {number} attemptGeneration
   * @param {string} [step] - the step that answered, for the message
   * @returns {PaymentError | null}
   */
  _visitorLeftError(attemptGeneration, step = 'the hold step') {
    if (this._paymentGeneration !== attemptGeneration) {
      return paymentError(
        Payment.ERROR_GENERATION_STALE,
        `The session ended before ${step} answered`
      );
    }
    if (this.isAborted) {
      return paymentError(
        Payment.ERROR_USER_CANCELLED,
        `Payment cancelled by user before ${step} answered`
      );
    }
    return null;
  }

  /**
   * Record the send to the reader until it answers. A reader reset during the
   * send waits for the answer: Stripe can put the action on the reader after
   * a cancel that overtook the send. Then the card prompt of a stopped
   * payment waits for the next visitor.
   *
   * @param {string} paymentIntentId
   * @param {Promise<unknown>} send
   */
  _watchReaderSend(paymentIntentId, send) {
    const readerSend = {
      paymentIntentId,
      // False only for the refusal of an offline reader: it took no action.
      tookAction: send.then(
        (sent) =>
          !(sent?.outcome === 'failed' && isReaderOffline(sent.failure)),
        (error) => !isReaderOffline(error?.failure)
      ),
    };
    this._readerSend = readerSend;
    readerSend.tookAction.then(() => {
      if (this._readerSend === readerSend) {
        this._readerSend = null;
      }
    });
  }

  _finishPayment() {
    this.isPaymentInProgress = false;
    this._resumeIdleWatcher();
  }

  _pauseIdleWatcher() {
    if (!this._idleWatcherPause) {
      this._idleWatcherPause = idleWatcher.pause();
    }
  }

  _resumeIdleWatcher() {
    if (this._idleWatcherPause) {
      this._idleWatcherPause.continue();
      this._idleWatcherPause = null;
    }
  }

  async _retryOnNetworkError(
    fn,
    retries = this.STRIPE_PAYMENT_RETRY_COUNT,
    operationName = 'unknown'
  ) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        // Only retry on genuine LOCAL network failures, not Stripe API errors.
        // Browser fetch throws TypeError with specific messages for network issues.
        // Stripe API errors have a `type` field (card_error, api_error, etc.) --
        // these must NOT be retried even if their message happens to contain
        // "network" (e.g., "card network unavailable").
        const msg = error.message?.toLowerCase() || '';
        // An error with an HTTP status comes from the backend and is not a
        // network failure. A 402 decline can have "network" in its text.
        const isServerResponse =
          Boolean(error.type) ||
          typeof error.context?.response?.status?.code === 'number';
        const isNetworkError =
          !isServerResponse &&
          (msg.includes('network') ||
            msg.includes('failed to fetch') ||
            msg.includes('load failed') ||
            msg.includes('networkerror') ||
            (error.name === 'TypeError' &&
              (msg.includes('fetch') || msg.includes('network'))));

        if (isNetworkError && attempt < retries) {
          console.warn(
            `[Payment] Network error on attempt ${attempt}, retrying...`
          );
          sentryService.addBreadcrumb(
            'payment',
            'Network error, retrying',
            {
              attempt: attempt,
              maxRetries: retries,
              errorMessage: error.message?.substring(0, 100),
              operation: operationName,
            },
            'warning'
          );
          sentryService.incrementCounter('payment.network_retry', 1, {
            attempt: String(attempt),
            operation: operationName,
          });
          await new Promise((resolve) =>
            setTimeout(resolve, this.STRIPE_PAYMENT_RETRY_DELAY * attempt)
          );
          // Check abort after delay -- if session was reset during the sleep,
          // don't retry into a stale context
          if (this.isAborted) {
            throw paymentError(
              Payment.ERROR_USER_CANCELLED,
              'Payment cancelled during retry delay'
            );
          }
        } else {
          // Track when all retries exhausted or non-network error
          if (isNetworkError) {
            sentryService.addBreadcrumb(
              'payment',
              'Network retries exhausted',
              {
                totalAttempts: attempt,
                maxRetries: retries,
                errorMessage: error.message?.substring(0, 100),
                operation: operationName,
              },
              'error'
            );
            sentryService.incrementCounter(
              'payment.network_retry_exhausted',
              1,
              {
                operation: operationName,
              }
            );
          }
          throw error;
        }
      }
    }
  }

  // Error codes for different payment failure scenarios
  static ERROR_TERMINAL_CANCELLED = 'TERMINAL_CANCELLED';
  static ERROR_TIMEOUT = 'TIMEOUT';
  static ERROR_USER_CANCELLED = 'USER_CANCELLED';
  static ERROR_GENERATION_STALE = 'GENERATION_STALE';
  static ERROR_READER_UNAVAILABLE = 'READER_UNAVAILABLE';
  static ERROR_CARD_FAILED = 'CARD_FAILED';

  waitForPaymentIntentReadyForCapture(
    paymentIntentId,
    accountId = null,
    failureAtSend = null,
    deadlineAt = this.clock.now() + this.STRIPE_PAYMENT_TIMEOUT_MS
  ) {
    return new Promise((resolve, reject) => {
      const clock = this.clock;
      let attempts = 0;
      let consecutiveNetworkErrors = 0;
      let pollTimer = null;
      let deadlineTimer = null;
      let settled = false;
      let hasPaymentStarted = false; // Track if card was presented to terminal
      const pollStartTime = clock.now();

      // Capture the generation at poll start. If reset() or a new processPayment()
      // bumps the counter while this poll is in-flight, stale callbacks detect the
      // mismatch and silently bail out instead of resolving/rejecting the (now
      // irrelevant) promise.
      const capturedGeneration = this._paymentGeneration;
      const isStale = () => this._paymentGeneration !== capturedGeneration;

      // Each end of the poll goes through settle(). Thus a status response
      // that comes after the deadline or after a stop does nothing.
      const settle = (finish) => {
        if (settled) {
          return;
        }
        settled = true;
        clock.clearTimeout(pollTimer);
        clock.clearTimeout(deadlineTimer);
        if (this._pollingCleanup === stop) {
          this._pollingCleanup = null;
        }
        finish();
      };
      const rejectWithCode = (code, message, failure = null) => {
        settle(() => reject(paymentError(code, message, failure)));
      };
      const rejectStale = () => {
        rejectWithCode(
          Payment.ERROR_GENERATION_STALE,
          'Payment polling cancelled: session or payment generation changed'
        );
      };

      // cancelPayment() and resetForNewSession() call this handle. It ends the
      // promise, thus processPayment() does not wait for a poll that stopped.
      const stop = () => {
        if (isStale()) {
          rejectStale();
          return;
        }
        rejectWithCode(Payment.ERROR_USER_CANCELLED, 'Payment polling stopped');
      };
      this._pollingCleanup = stop;

      const scheduleNextCheck = () => {
        pollTimer = clock.setTimeout(
          checkStatus,
          this.STRIPE_PAYMENT_CHECK_INTERVAL
        );
      };

      // The deadline has its own timer, thus a slow status request cannot
      // make the card window longer.
      const onDeadline = () => {
        if (isStale()) {
          rejectStale();
          return;
        }
        const timeoutMinutes = Math.round(
          this.STRIPE_PAYMENT_TIMEOUT_MS / 60000
        );
        console.warn(
          `[Payment] Payment timed out after ${timeoutMinutes} minutes (${attempts} attempts)`
        );
        sentryService.addBreadcrumb(
          'payment',
          'Payment polling timed out',
          this._sanitizeForSentry({
            paymentIntentId,
            pollAttempts: attempts,
            timeoutMinutes,
          }),
          'error'
        );
        sentryService.incrementCounter('payment.timeout', 1, {
          timeout_seconds: Math.round(this.STRIPE_PAYMENT_TIMEOUT_MS / 1000),
          poll_attempts: attempts,
          payment_destination: this.paymentDestination,
        });
        rejectWithCode(
          Payment.ERROR_TIMEOUT,
          `Payment timed out after ${timeoutMinutes} minutes`
        );
      };
      deadlineTimer = clock.setTimeout(
        onDeadline,
        Math.max(0, deadlineAt - clock.now())
      );

      const checkStatus = () => {
        if (settled) {
          return;
        }
        if (isStale()) {
          rejectStale();
          return;
        }

        attempts++;

        this._retryOnNetworkError(
          () => restApi.checkPaymentIntentStatus(paymentIntentId, accountId),
          this.STRIPE_PAYMENT_RETRY_COUNT,
          'checkPaymentIntentStatus'
        )
          .then((paymentIntent) => {
            if (settled) {
              return;
            }
            if (isStale()) {
              rejectStale();
              return;
            }
            consecutiveNetworkErrors = 0; // Reset on success

            const isPaymentReadyForCapture = [
              'succeeded',
              'requires_capture',
            ].includes(paymentIntent.status);
            const isPaymentInProgress = [
              'requires_confirmation',
              'processing',
            ].includes(paymentIntent.status);

            // Detailed logging for debugging terminal behavior. The backend
            // now surfaces a differentiated failure envelope; a card decline
            // (outcome === 'failed') is what the old last_payment_error flagged.
            const failure = paymentIntent.failure || null;

            // Log card decline separately for visibility
            if (failure) {
              // Remember it so a later captureException (timeout/network) can
              // still report why the terminal was failing — the polled decline
              // PI never reaches `lastKnownPaymentIntent` otherwise.
              this._lastPaymentFailure = failure;
              console.warn('[Payment] Card error detected:', {
                code: failure.code,
                decline_code: failure.decline_code,
                category: failure.category,
                message: this._sanitizeErrorMessage(failure.message)?.substring(
                  0,
                  50
                ),
              });

              // Track card decline in Sentry (not as error, just breadcrumb)
              sentryService.addBreadcrumb(
                'payment',
                'Card error detected',
                this._sanitizeForSentry({
                  paymentIntentId,
                  errorCode: failure.code,
                  declineCode: failure.decline_code,
                  errorCategory: failure.category,
                }),
                'warning'
              );
            }

            // Track if payment has started (card presented to terminal)
            if (isPaymentInProgress || isPaymentReadyForCapture) {
              if (!hasPaymentStarted) {
                clientEvents.record(
                  'payment_card_presented',
                  {
                    payment_intent_id: paymentIntentId,
                    status: paymentIntent.status,
                    poll_attempt: attempts,
                    duration_ms: clock.now() - pollStartTime,
                  },
                  { area: 'payment' }
                );
                sentryService.addBreadcrumb(
                  'payment',
                  'Card presented to terminal',
                  this._sanitizeForSentry({
                    paymentIntentId,
                    status: paymentIntent.status,
                    pollAttempt: attempts,
                  })
                );
                // Track card presentation metrics
                sentryService.trackMetric(
                  'payment.card_presentation_time_ms',
                  clock.now() - pollStartTime,
                  'millisecond',
                  { payment_destination: this.paymentDestination }
                );
                sentryService.incrementCounter('payment.card_presented', 1);
              }
              hasPaymentStarted = true;
            }

            // The reader ends its action after a card failure and does not ask
            // for a different card. Thus the poll stops, and the visitor sees
            // the cause and can try again.
            if (
              failure !== null &&
              paymentIntent.status === 'requires_payment_method' &&
              isNewCardFailure(failure, failureAtSend, hasPaymentStarted)
            ) {
              sentryService.addBreadcrumb(
                'payment',
                'Card failed, poll stopped',
                this._sanitizeForSentry({
                  paymentIntentId,
                  declineCode: failure.decline_code,
                  errorCategory: failure.category,
                  attemptId: failure.attempt_id,
                }),
                'warning'
              );
              rejectWithCode(
                Payment.ERROR_CARD_FAILED,
                failure.message || 'Card payment failed',
                failure
              );
              return;
            }

            // A status that returns to requires_payment_method with no new
            // failure after a card was presented means a cancel on the reader.
            if (
              hasPaymentStarted &&
              paymentIntent.status === 'requires_payment_method'
            ) {
              console.warn(
                '[Payment] Terminal cancellation detected - payment started but reverted without error'
              );
              sentryService.addBreadcrumb(
                'payment',
                'Terminal cancellation detected',
                this._sanitizeForSentry({
                  paymentIntentId,
                  pollAttempts: attempts,
                  pollDurationMs: clock.now() - pollStartTime,
                }),
                'warning'
              );
              rejectWithCode(
                Payment.ERROR_TERMINAL_CANCELLED,
                'Payment cancelled on terminal'
              );
              return;
            }

            // A canceled intent is terminal (outcome=failed, category=canceled)
            // — fast-fail instead of polling to the 2-minute timeout on a
            // definitively-dead intent.
            if (
              paymentIntent.status === 'canceled' ||
              paymentIntent.status === 'cancelled'
            ) {
              rejectWithCode(
                Payment.ERROR_TERMINAL_CANCELLED,
                'Payment canceled on terminal'
              );
              return;
            }

            // For Terminal payments, requires_confirmation is a transient state
            // The terminal handles confirmation automatically - just continue polling
            if (paymentIntent.status === 'requires_confirmation') {
              scheduleNextCheck();
            } else if (isPaymentReadyForCapture) {
              // CRITICAL: Check isAborted BEFORE resolving. If session.end()
              // was called while this HTTP response was in-flight, we must NOT
              // resolve (which would trigger capture on a dead session).
              if (this.isAborted) {
                sentryService.addBreadcrumb(
                  'payment',
                  'Payment ready for capture but session aborted -- discarding',
                  this._sanitizeForSentry({ paymentIntentId }),
                  'warning'
                );
                rejectWithCode(
                  Payment.ERROR_USER_CANCELLED,
                  'Payment cancelled by user (aborted during capture-ready poll)'
                );
                return;
              }

              const pollDurationMs = clock.now() - pollStartTime;
              sentryService.addBreadcrumb(
                'payment',
                'Payment ready for capture',
                this._sanitizeForSentry({
                  paymentIntentId,
                  status: paymentIntent.status,
                  pollAttempts: attempts,
                  pollDurationMs,
                })
              );
              sentryService.trackMetric(
                'payment.poll_duration_ms',
                pollDurationMs,
                'millisecond'
              );
              sentryService.trackMetric(
                'payment.poll_attempts',
                attempts,
                'none'
              );

              settle(() => resolve(paymentIntent));
            } else if (this.isAborted) {
              sentryService.addBreadcrumb(
                'payment',
                'Payment aborted by user during polling',
                this._sanitizeForSentry({
                  paymentIntentId,
                  pollAttempts: attempts,
                })
              );
              rejectWithCode(
                Payment.ERROR_USER_CANCELLED,
                'Payment cancelled by user'
              );
            } else {
              // Still waiting for payment - continue polling
              // (requires_payment_method status is expected during card tap wait)
              scheduleNextCheck();
            }
          })
          .catch((error) => {
            if (settled) {
              return;
            }
            if (isStale()) {
              rejectStale();
              return;
            }
            consecutiveNetworkErrors++;

            sentryService.addBreadcrumb(
              'payment',
              'Payment status check failed',
              this._sanitizeForSentry({
                paymentIntentId,
                errorMessage: error.message,
                consecutiveNetworkErrors,
                maxRetries: this.STRIPE_PAYMENT_RETRY_COUNT,
              }),
              'warning'
            );

            if (consecutiveNetworkErrors >= this.STRIPE_PAYMENT_RETRY_COUNT) {
              settle(() =>
                reject(
                  new Error(
                    `Payment status check failed after multiple retries: ${this._sanitizeErrorMessage(error.message)}`
                  )
                )
              );
            } else if (this.isAborted) {
              rejectWithCode(
                Payment.ERROR_USER_CANCELLED,
                'Payment cancelled by user'
              );
            } else {
              // Continue polling even after transient errors
              console.warn(
                `[Payment] Status check error (${consecutiveNetworkErrors}/${this.STRIPE_PAYMENT_RETRY_COUNT}):`,
                this._sanitizeErrorMessage(error.message)
              );
              scheduleNextCheck();
            }
          });
      };

      checkStatus();
    });
  }

  /**
   * Cancel the action on the reader. The kiosk will not accept this payment
   * (the cancel button, or the card screen closed), thus the backend also
   * releases a held amount of the intent on the reader. After the card paid,
   * it releases the paid hold that no hold step accepted. A hold step that
   * answers after the cancel does not print. The kiosk sends one release for
   * each paid hold.
   */
  cancelPayment() {
    // Guard against multiple cancel calls (from onClick, polling loop, and beforeUnmount)
    if (this.isAborted) {
      return this._cancelInFlight ?? Promise.resolve();
    }
    this.isAborted = true;
    const releaseHoldOf = this._readerPaymentIntentId ?? this._paidHoldIntentId;

    // Immediately kill any active polling setTimeout to prevent stale poll
    // callbacks from running after cancellation
    if (this._pollingCleanup) {
      this._pollingCleanup();
      this._pollingCleanup = null;
    }
    const readerId = authConfig.getStripeReaderId();

    // No reader configured — nothing to cancel on the physical terminal.
    // Skip the API call entirely to avoid a pointless request with reader_id: null.
    if (!readerId) {
      sentryService.addBreadcrumb(
        'payment',
        'Cancel skipped: no reader configured',
        {},
        'info'
      );
      return Promise.resolve();
    }

    // For partner mode, pass the connected account ID so Stripe finds the correct reader
    const accountId =
      this.paymentDestination === 'partner' ? this.stripeAccountId : null;

    // Track cancellation request
    clientEvents.record(
      'payment_cancelled',
      {
        reason: 'cancel_requested',
        reader_id: readerId,
        payment_destination: this.paymentDestination,
      },
      { area: 'payment', level: 'warning' }
    );
    sentryService.addBreadcrumb(
      'payment',
      'Cancel payment requested',
      this._sanitizeForSentry({
        readerId,
        accountId,
        paymentDestination: this.paymentDestination,
        releaseHoldOf,
      })
    );
    sentryService.incrementCounter('payment.cancel_requested', 1, {
      payment_destination: this.paymentDestination,
    });

    this._paidHoldIntentId = null;
    return this._resetReader('cancel', releaseHoldOf);
  }

  /**
   * Cancel the action on the reader at the end of a card interaction. The
   * backend accepts this call for a reader that has no action.
   *
   * The returned promise never rejects, and it does not block the visitor: a
   * failed reset goes to Sentry. processPayment() waits for `_cancelInFlight`
   * before it sends the next intent, because the cancel is for the reader and
   * not for one intent. A cancel that comes after the next send can stop the
   * next payment.
   *
   * @param {string} reason
   * @param {string | null} [releaseHoldOf] - the intent on the reader that
   *   the kiosk did not accept. After the cancel, the backend releases a held
   *   amount of it. The backend never releases an accepted hold.
   * @returns {Promise<void>}
   */
  _resetReader(reason, releaseHoldOf = null) {
    const marker = readReaderActionMarker();
    const readerId = marker?.reader_id ?? authConfig.getStripeReaderId();
    if (!readerId) {
      return this._cancelInFlight ?? Promise.resolve();
    }
    const configuredAccountId =
      this.paymentDestination === 'partner' ? this.stripeAccountId : null;
    const accountId = marker ? marker.account_id : configuredAccountId;
    const paymentIntentId =
      releaseHoldOf ?? marker?.payment_intent_id ?? undefined;
    sentryService.addBreadcrumb(
      'payment',
      'Reader reset requested',
      this._sanitizeForSentry({
        readerId,
        paymentIntentId,
        reason,
        releaseHoldOf,
      })
    );

    /** @param {string} resetReason */
    const cancelOnReader = async (resetReason) => {
      try {
        const response = await sentryService.withSpan(
          'payment.cancel',
          'payment.stripe',
          // The session and intent ids let the backend find the Stripe
          // account without a probe of each connected account.
          async () =>
            restApi.cancelPaymentOnReader(readerId, accountId, {
              sessionId: store.sessionId ?? undefined,
              paymentIntentId,
              ...(releaseHoldOf ? { releaseHold: true } : {}),
            }),
          {
            'payment.reader_id': readerId,
            'payment.reset_reason': resetReason,
          }
        );
        clearReaderActionMarker(marker);
        sentryService.addBreadcrumb(
          'payment',
          'Cancel payment succeeded',
          this._sanitizeForSentry({
            readerId,
            reason: resetReason,
            releaseHoldOf,
            paymentIntentStatus: response?.payment_intent_status ?? null,
          })
        );
      } catch (error) {
        console.warn(
          '[Payment] Failed to cancel payment on reader:',
          this._sanitizeErrorMessage(error?.message)
        );
        sentryService.captureMessage('Card reader reset failed', {
          tags: {
            payment_error: 'reader_reset_failed',
            reset_reason: resetReason,
          },
          extra: this._getPaymentContext({
            reader_id: readerId,
            payment_intent_id: paymentIntentId,
            http_status: error?.context?.response?.status?.code,
            reader_failure_code: error?.failure?.code,
            errorMessage: error?.message,
          }),
          level: 'warning',
        });
      }
    };

    const readerSend = this._readerSend;
    let resetBeforeSendAnswer = false;
    const request = this._sendAnswerWithin(readerSend).then((tookAction) => {
      if (tookAction === null) {
        resetBeforeSendAnswer = true;
      } else if (
        !tookAction &&
        readerSend?.paymentIntentId === paymentIntentId
      ) {
        // The offline reader refused this intent: no action and no hold.
        clearReaderActionMarker(marker);
        sentryService.addBreadcrumb(
          'payment',
          'Reader reset skipped: the offline reader took no action',
          this._sanitizeForSentry({ readerId, reason, paymentIntentId })
        );
        return;
      }
      return cancelOnReader(reason);
    });

    const tracked = this._trackCancel(request);
    if (readerSend) {
      // A send that answers after the reset can put its action on the reader.
      // The next send waits for this second reset, but the caller does not.
      this._trackCancel(
        request
          .then(() => readerSend.tookAction)
          .then((tookAction) =>
            resetBeforeSendAnswer && tookAction
              ? cancelOnReader(`${reason}_late_send`)
              : undefined
          )
      );
    }
    return tracked;
  }

  /**
   * The answer of a send to the reader: true if the send can have put an
   * action on the reader, false if it did not, and null if it gave no answer
   * in READER_SEND_WAIT_MS.
   *
   * @param {{ tookAction: Promise<boolean> } | null} readerSend
   * @returns {Promise<boolean | null>}
   */
  _sendAnswerWithin(readerSend) {
    if (!readerSend) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      const timer = this.clock.setTimeout(
        () => resolve(null),
        READER_SEND_WAIT_MS
      );
      readerSend.tookAction.then((tookAction) => {
        this.clock.clearTimeout(timer);
        resolve(tookAction);
      });
    });
  }

  /**
   * Add a reset to the resets that the next send waits for.
   *
   * @param {Promise<unknown>} reset
   * @returns {Promise<void>}
   */
  _trackCancel(reset) {
    const previous = this._cancelInFlight;
    const tracked = previous
      ? Promise.all([previous, reset]).then(() => undefined)
      : reset.then(() => undefined);
    this._cancelInFlight = tracked;
    tracked.then(() => {
      if (this._cancelInFlight === tracked) {
        this._cancelInFlight = null;
      }
    });
    return tracked;
  }
}

export const payment = new Payment();
